<script setup>
import img01 from '@/assets/images/userguide/01_login_pincode.png'
import img02 from '@/assets/images/userguide/02_hovedskjerm.png'
import img03 from '@/assets/images/userguide/03_spise_her.png'
import img04 from '@/assets/images/userguide/04_bordplan.png'
import img05 from '@/assets/images/userguide/05_handlekurv.png'
import img06 from '@/assets/images/userguide/06_betaling.png'
import img07 from '@/assets/images/userguide/07_kontant_betaling.png'
import img08 from '@/assets/images/userguide/08_ordreliste.png'
import img09 from '@/assets/images/userguide/09_kanseller_ordre.png'
import img10 from '@/assets/images/userguide/10_refunder_ordre.png'
import img11 from '@/assets/images/userguide/11_dagsoppgjor.png'
import img12 from '@/assets/images/userguide/12_dagsoppgjor_opptelling.png'
import img13 from '@/assets/images/userguide/13_ta_med.png'
import pdfUrl from '@/assets/pdf/Brukerveiledning_TastyPOS.pdf'

function scrollTo(event) {
    const href = event.target.getAttribute('href');
    if (href && href.startsWith('#')) {
        event.preventDefault();
        const el = document.getElementById(href.substring(1));
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    }
}
</script>

<template>
<div class="bv-wrapper">
<!-- Header -->
<div class="header">
    <h1>Brukerveiledning TastyPOS</h1>
    <p class="subtitle">Systemskildring av kassasystemet og alle funksjoner</p>
    <div class="header-meta">
        <span>Versjon 1.0</span>
        <span>02. januar 2026</span>
        <a :href="pdfUrl" download="Brukerveiledning_TastyPOS.pdf" class="pdf-download">Last ned PDF</a>
        <span>CAGISTA AS</span>
    </div>
</div>

<div class="container">

<!-- Sidebar -->
<aside class="sidebar">
    <nav class="toc" @click="scrollTo">
        <h3>Innholdsfortegnelse</h3>
        <ol>
            <li><a href="#kap1">Innledning</a></li>
            <li><a href="#kap2">Systemarkitektur</a></li>
            <li><a href="#kap3">Innlogging</a></li>
            <li><a href="#kap4">Hovedskjerm</a></li>
            <li><a href="#kap5">Meny og produkter</a></li>
            <li><a href="#kap6">Ordrehåndtering</a></li>
            <li><a href="#kap7">Betalingssystemet</a></li>
            <li><a href="#kap8">Bordadministrasjon</a></li>
            <li><a href="#kap9">Kvitteringer og utskrift</a></li>
            <li><a href="#kap10">Kassaskuff</a></li>
            <li><a href="#kap11">Rapporter</a></li>
            <li><a href="#kap12">Dagstart og dagslutt</a></li>
            <li><a href="#kap13">Kontantuttak</a></li>
            <li><a href="#kap14">Kansellering og retur</a></li>
            <li><a href="#kap15">Kundedisplay</a></li>
            <li><a href="#kap16">Elektronisk journal</a></li>
            <li><a href="#kap17">SAF-T kassasystem</a></li>
            <li><a href="#kap18">Maskinvareintegrasjon</a></li>
            <li><a href="#kap19">Innstillinger</a></li>
            <li><a href="#kap20">Synkronisering</a></li>
            <li><a href="#kap21">Arbeidsflyter</a></li>
        </ol>
    </nav>
</aside>

<!-- Main Content -->
<main class="main">

<!-- 1. Innledning -->
<div class="section" id="kap1">
    <h2><span class="chapter-num">1</span> Innledning</h2>
    <p>TastyPOS er et kassasystem (Point of Sale) utviklet for restaurant- og serveringsbransjen. Systemet kjører på Android-enheter (minimum Android 11) og er optimalisert for Sunmi-terminaler med innebygd kvitteringsskriver og valgfri kundeskjerm.</p>
    <p>Systemet dekker hele arbeidsflyten for en serveringsvirksomhet, fra ordremottak og kjøkkenhåndtering til betaling, rapportering og skattemessig etterlevelse i henhold til norske krav (SAF-T kassasystem).</p>

    <h3>Systemkrav</h3>
    <table>
        <thead><tr><th>Krav</th><th>Spesifikasjon</th></tr></thead>
        <tbody>
            <tr><td><strong>Operativsystem</strong></td><td>Android 11 (API 30) eller nyere</td></tr>
            <tr><td><strong>Anbefalt maskinvare</strong></td><td>Sunmi V2s, Sunmi D3 Mini, Sunmi V3 Mix</td></tr>
            <tr><td><strong>Nettverkstilkobling</strong></td><td>Wi-Fi eller mobildata (for synkronisering)</td></tr>
            <tr><td><strong>Betalingsterminal</strong></td><td>SoftPay eller Viva (for kortbetaling)</td></tr>
        </tbody>
    </table>
</div>

<!-- 2. Systemarkitektur -->
<div class="section" id="kap2">
    <h2><span class="chapter-num">2</span> Systemarkitektur og oppbygging</h2>

    <h3>2.1 Overordnet oppbygging</h3>
    <p>TastyPOS er bygget opp med følgende hovedkomponenter:</p>
    <table>
        <thead><tr><th>Komponent</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Kassemodul</strong></td><td>Hovedskjermen for ordremottak, menynavigasjon og handlekurv</td></tr>
            <tr><td><strong>Betalingsmodul</strong></td><td>Håndtering av alle betalingsmetoder og transaksjoner</td></tr>
            <tr><td><strong>Ordremodul</strong></td><td>Oversikt over alle ordrer med filtrering og statussporing</td></tr>
            <tr><td><strong>Bordmodul</strong></td><td>Visuell plantegning med bord for Spise Her</td></tr>
            <tr><td><strong>Rapportmodul</strong></td><td>X-rapport (mellomrapport) og Z-rapport (dagsoppgjør)</td></tr>
            <tr><td><strong>Administrasjonsmodul</strong></td><td>Innstillinger, brukere, terminalkonfigurasjon</td></tr>
            <tr><td><strong>Elektronisk journal</strong></td><td>Loggføring av alle transaksjoner og hendelser</td></tr>
            <tr><td><strong>Utskriftsmodul</strong></td><td>Kvitteringsutskrift, kjøkkenbestilling, testutskrift</td></tr>
            <tr><td><strong>Maskinvareintegrasjon</strong></td><td>Skriver, kassaskuff, betalingsterminal, kundedisplay</td></tr>
        </tbody>
    </table>

    <h3>2.2 Databasesystem</h3>
    <p>TastyPOS bruker Couchbase Lite som lokal database. Dette er en dokumentbasert database som lagrer data lokalt på enheten og synkroniserer med skytjenesten (CAGISTA Cloud) via Couchbase Sync Gateway.</p>
    <div class="info-box">
        <strong>Dokumenttyper i databasen:</strong> Lokasjon, Kategori, Vare, Tilleggsgruppe, Tillegg, Ordre, Betaling, Bruker, MVA-sats, Plantegning, Bord, Rabatt, Kvittering, Z-rapport, Terminal, Firma, Elektronisk journal.
    </div>

    <h3>2.3 Sikkerhetsarkitektur</h3>
    <ul>
        <li>PIN-kode-autentisering for daglig bruk</li>
        <li>Brukernavn/passord for administratorinnlogging</li>
        <li>Automatisk utlogging etter 7 timers inaktivitet</li>
        <li>Terminal- og lokasjonsbasert tilgangskontroll</li>
        <li>Kryptering av sensitive data</li>
    </ul>
</div>

<!-- 3. Innlogging -->
<div class="section" id="kap3">
    <h2><span class="chapter-num">3</span> Innlogging og brukeradministrasjon</h2>

    <h3>3.1 Første gangs innlogging (Merchant-innlogging)</h3>
    <p>Ved første oppsett eller ny installasjon logges det inn med forhandlerkonto:</p>
    <ol>
        <li><strong>Oppstartsskjerm</strong> – Appen viser en velkomstskjerm ved oppstart</li>
        <li><strong>Introduksjonsskjerm</strong> – Kort gjennomgang av systemet</li>
        <li><strong>Innlogging med brukernavn og passord</strong> – Forhandlerkontoen brukes for å koble terminalen til riktig organisasjon</li>
        <li><strong>Valg av lokasjon</strong> – Etter innlogging velges den aktuelle lokasjonen/filialen terminalen skal tilhøre</li>
    </ol>

    <h3>3.2 Daglig innlogging (PIN-kode)</h3>
    <p>For daglig bruk logger operatører inn med en 4-sifret PIN-kode:</p>
    <ol>
        <li>Skjermen viser system-ID og terminal-ID</li>
        <li>Operatøren taster inn sin personlige 4-sifrede PIN-kode</li>
        <li>Ved korrekt PIN-kode åpnes kassesystemet</li>
    </ol>

    <figure class="screenshot">
        <img :src="img01" alt="PIN-kode innlogging">
        <figcaption>Figur 1: PIN-kode innloggingsskjermen med numerisk tastatur. Øverst til høyre vises ID-nummer, KasseID, versjonsnummer og leverandør. Nederst vises synkroniseringsstatus med CAGISTA Cloud.</figcaption>
    </figure>

    <h3>3.3 Automatisk utlogging</h3>
    <ul>
        <li>Systemet logger automatisk ut operatøren etter <strong>7 timers</strong> inaktivitet</li>
        <li>Operatøren blir varslet om automatisk utlogging</li>
        <li>Ny innlogging kreves med PIN-kode</li>
    </ul>

    <h3>3.4 Brukeregenskaper</h3>
    <p>Hver bruker i systemet har følgende egenskaper:</p>
    <ul>
        <li>Fornavn og etternavn</li>
        <li>E-postadresse</li>
        <li>PIN-kode for hurtiginnlogging</li>
        <li>Brukerrolle</li>
        <li>Tilordnet lokasjon</li>
        <li>Innloggingsstatus (sporbarhet)</li>
    </ul>
</div>

<!-- 4. Hovedskjerm -->
<div class="section" id="kap4">
    <h2><span class="chapter-num">4</span> Hovedskjerm og navigasjon</h2>

    <h3>4.1 Hovedskjermen (Kasseskjerm)</h3>
    <p>Hovedskjermen er kassens primære arbeidsflate og består av følgende elementer:</p>
    <ul>
        <li><strong>Kategorinavigasjon</strong> – Venstre side; viser produktkategorier for rask navigasjon (Sushi, Bubble tea, Viet, Drikke m.m.)</li>
        <li><strong>Produktvisning</strong> – Sentral del av skjermen; viser varer i valgt kategori med bilder, navn og pris</li>
        <li><strong>Handlekurv</strong> – Høyre side; viser valgte varer med antall, pris, tillegg og totalbeløp</li>
        <li><strong>Ordretypevalg</strong> – «SPISE HER» og «TA MED»-knapper øverst til høyre</li>
        <li><strong>Toppmeny</strong> – ORDRER, KANSELLER ORDRE, REFUNDER ORDRE, ÅPNE KASSESKUFF, TA DAGSOPPGJØR</li>
        <li><strong>Bunnmeny</strong> – KOMMENTAR, BORD (kun Spise Her), RABATT, NY ORDRE, LAGRE ORDRE, BETAL</li>
        <li><strong>Søkefunksjon</strong> – Søkefelt øverst for å finne ordrer</li>
    </ul>

    <figure class="screenshot">
        <img :src="img02" alt="Hovedskjerm – Ta med">
        <figcaption>Figur 2: Hovedskjermen i «TA MED»-modus. Kategorier til venstre, produkter med bilder og priser i midten, handlekurv til høyre. MVA 15 % inkludert. Utsolgte varer markeres med «Utsolgt»-stempel.</figcaption>
    </figure>

    <figure class="screenshot">
        <img :src="img03" alt="Hovedskjerm – Spise her">
        <figcaption>Figur 3: Hovedskjermen i «SPISE HER»-modus. Legg merke til høyere priser (Spise Her-priser) og MVA 25 %. «BORD»-knappen vises nederst for valg av bord.</figcaption>
    </figure>

    <figure class="screenshot">
        <img :src="img13" alt="Hovedskjerm med handlekurv">
        <figcaption>Figur 4: Hovedskjermen i «TA MED»-modus med varer i handlekurven. Ordrenummeret vises øverst i handlekurven. Hver varelinje viser pris, antallskontroll (+/−), rabattknapp (%) og sletteknapp.</figcaption>
    </figure>

    <h3>4.2 Navigasjonsstruktur</h3>
    <p>Fra hovedskjermen kan operatøren navigere til følgende funksjoner via toppmenyen:</p>
    <ul>
        <li><strong>ORDRER</strong> – Oversikt over alle aktive og fullførte ordrer med filtrering</li>
        <li><strong>KANSELLER ORDRE</strong> – Kansellering av ubetalte ordrer</li>
        <li><strong>REFUNDER ORDRE</strong> – Refundering av betalte ordrer</li>
        <li><strong>ÅPNE KASSESKUFF</strong> – Manuell åpning av kassaskuffen</li>
        <li><strong>TA DAGSOPPGJØR</strong> – Start dagsluttrutinen med opptelling og Z-rapport</li>
    </ul>
</div>

<!-- 5. Meny og produkter -->
<div class="section" id="kap5">
    <h2><span class="chapter-num">5</span> Meny- og produkthåndtering</h2>

    <h3>5.1 Produkttyper</h3>
    <table>
        <thead><tr><th>Type</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Rett (DISH)</strong></td><td>Matretter</td></tr>
            <tr><td><strong>Drikke (DRINK)</strong></td><td>Drikkevarer</td></tr>
            <tr><td><strong>Tillegg (ADD_ON)</strong></td><td>Tilleggsvalg/modifikatorer (f.eks. ekstra ost, saus)</td></tr>
            <tr><td><strong>Leveringstjeneste (DELIVERY_SERVICE)</strong></td><td>Leveringsgebyr</td></tr>
        </tbody>
    </table>

    <h3>5.2 Produktegenskaper</h3>
    <p>Hvert produkt har følgende egenskaper:</p>
    <ul>
        <li><strong>Navn</strong> – Produktets visningsnavn</li>
        <li><strong>Beskrivelse</strong> – Detaljert beskrivelse</li>
        <li><strong>Kategori</strong> – Hvilken kategori produktet tilhører</li>
        <li><strong>Pris (henting/levering)</strong> – Pris ved henting eller levering</li>
        <li><strong>Pris (Spise Her)</strong> – Separat pris for Spise Her (kan avvike)</li>
        <li><strong>Bilde</strong> – Produktbilde</li>
        <li><strong>Ingredienser</strong> – Ingrediensliste</li>
        <li><strong>Allergener</strong> – Allergeninformasjon</li>
        <li><strong>Tilgjengelighet</strong> – Om produktet er tilgjengelig eller utsolgt</li>
        <li><strong>Rabattberettigelse</strong> – Om produktet kan inngå i rabattordninger</li>
    </ul>

    <h3>5.3 Tillegg og modifikatorer</h3>
    <p>Produkter kan ha tilknyttede tilleggsgrupper:</p>
    <ul>
        <li><strong>Tilleggsgrupper</strong> – F.eks. «Størrelse», «Saus», «Ekstra tilbehør»</li>
        <li><strong>Obligatoriske/valgfrie tillegg</strong> – Noen tillegg må velges, andre er valgfrie</li>
        <li><strong>Flervalg</strong> – Kunden kan velge flere tillegg fra samme gruppe</li>
        <li><strong>Tilleggspris</strong> – Hvert tillegg kan ha egen pris</li>
        <li><strong>Antallskontroll</strong> – Antall per tillegg kan justeres</li>
    </ul>

    <h3>5.4 Varianter</h3>
    <p>Produkter kan ha varianter, for eksempel ulike størrelser eller tilberedningsmetoder, med egne priser per ordretype.</p>

    <h3>5.5 Tilgjengelighetstyring</h3>
    <p>Produkttilgjengelighet kan styres på flere nivåer:</p>
    <ul>
        <li><strong>Kanal</strong> – Nettbutikk, kiosk, eller kasse (POS)</li>
        <li><strong>Ordretype</strong> – Spise Her, henting, eller levering</li>
        <li><strong>Dag/tidspunkt</strong> – Tilgjengelig kun på bestemte dager/tidspunkter</li>
        <li><strong>Lokasjon</strong> – Lokasjonsspesifikk tilgjengelighet</li>
    </ul>
</div>

<!-- 6. Ordrehåndtering -->
<div class="section" id="kap6">
    <h2><span class="chapter-num">6</span> Ordrehåndtering</h2>

    <h3>6.1 Ordretyper</h3>
    <table>
        <thead><tr><th>Ordretype</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Spise Her (DINE_IN)</strong></td><td>Kunden spiser i restauranten. Krever valg av bord.</td></tr>
            <tr><td><strong>Henting (PICKUP)</strong></td><td>Kunden henter maten selv. Hentetidspunkt kan settes.</td></tr>
            <tr><td><strong>Levering (DELIVERY)</strong></td><td>Maten leveres til kundens adresse. Leveringsadresse kreves.</td></tr>
        </tbody>
    </table>

    <h3>6.2 Ordrestatus</h3>
    <p>En ordre gjennomgår følgende statuser:</p>
    <div class="status-flow">
        <span class="status-badge status-draft">UTKAST</span>
        <span class="status-arrow">&#10140;</span>
        <span class="status-badge status-ordered">BESTILT</span>
        <span class="status-arrow">&#10140;</span>
        <span class="status-badge status-confirmed">BEKREFTET</span>
        <span class="status-arrow">&#10140;</span>
        <span class="status-badge status-completed">FULLFØRT</span>
    </div>
    <p>I tillegg finnes:</p>
    <div class="status-flow">
        <span class="status-badge status-cancelled">KANSELLERT</span>
        <span class="status-badge status-refunded">REFUNDERT</span>
        <span class="status-badge status-refunded">DELVIS REFUNDERT</span>
    </div>

    <h3>6.3 Opprette en ordre</h3>
    <ol>
        <li><strong>Velg ordretype</strong> – Spise Her, Henting eller Levering</li>
        <li><strong>Velg bord</strong> (kun Spise Her) – Velg bord fra plantegningen</li>
        <li><strong>Angi antall gjester</strong> (Spise Her)</li>
        <li><strong>Legg til varer</strong> – Bla gjennom kategorier eller søk, trykk på varer for å legge til</li>
        <li><strong>Velg tillegg</strong> – Hvis varen har tilleggsgrupper, velg ønskede tillegg</li>
        <li><strong>Juster antall</strong> – Øk eller reduser antall i handlekurven</li>
        <li><strong>Legg til kommentar</strong> – Valgfri kommentar til ordren eller enkeltlinjer</li>
        <li><strong>Gå til betaling</strong> – Trykk «Betal» for å fullføre ordren</li>
    </ol>

    <figure class="screenshot">
        <img :src="img05" alt="Handlekurv med varer">
        <figcaption>Figur 5: Handlekurven med to varer i «SPISE HER»-modus. Hver varelinje har knapper for rabatt (%), reduser antall (−), øk antall (+) og slett. Totalbeløpet med MVA vises nederst.</figcaption>
    </figure>

    <h3>6.4 Kundeinformasjon</h3>
    <h4>Henting:</h4>
    <ul>
        <li>Kundens navn</li>
        <li>Mobilnummer</li>
        <li>Hentetidspunkt (valgfritt): «Når maten er klar» (standard) eller spesifikt tidspunkt</li>
    </ul>
    <h4>Levering:</h4>
    <ul>
        <li>Kundens navn og mobilnummer</li>
        <li>Leveringsadresse: gateadresse, postnummer og by</li>
        <li>Leveringstidspunkt: «Så snart som mulig» (standard) eller spesifikt tidspunkt</li>
    </ul>

    <h3>6.5 Ordreliste og filtrering</h3>
    <table>
        <thead><tr><th>Filter</th><th>Viser</th></tr></thead>
        <tbody>
            <tr><td><strong>Ikke betalt</strong></td><td>Ordrer som venter på betaling</td></tr>
            <tr><td><strong>Bestilt</strong></td><td>Ordrer sendt til kjøkken</td></tr>
            <tr><td><strong>Bekreftet</strong></td><td>Ordrer bekreftet av kjøkken</td></tr>
            <tr><td><strong>Fullført</strong></td><td>Ferdigstilte ordrer</td></tr>
            <tr><td><strong>Alle</strong></td><td>Samtlige ordrer</td></tr>
        </tbody>
    </table>

    <figure class="screenshot">
        <img :src="img08" alt="Ordreliste">
        <figcaption>Figur 6: Ordrelisten med filtreringsfaner øverst. Hvert ordrekort viser ordrenummer, ordretype, beløp med MVA, status, betalingsstatus, kilde og tidspunkt.</figcaption>
    </figure>

    <h3>6.6 Rabatter</h3>
    <ul>
        <li><strong>Manuell rabatt i prosent</strong> – F.eks. 10 % på hele ordren</li>
        <li><strong>Linjerabatt</strong> – Rabatt på enkeltlinjer i ordren</li>
        <li><strong>Lojalitetsrabatt</strong> – Rabatt basert på oppsparte lojalitetspoeng</li>
        <li><strong>100 % rabattvarsel</strong> – Systemet varsler ved full rabatt (0 kr å betale)</li>
    </ul>
</div>

<!-- 7. Betalingssystemet -->
<div class="section" id="kap7">
    <h2><span class="chapter-num">7</span> Betalingssystemet</h2>

    <h3>7.1 Betalingsmetoder</h3>
    <table>
        <thead><tr><th>Metode</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Kontant</strong></td><td>Kontantbetaling med vekselberegning</td></tr>
            <tr><td><strong>Kort</strong></td><td>Debet-/kredittkort via SoftPay eller Viva betalingsterminal</td></tr>
            <tr><td><strong>Kontant + Kort</strong></td><td>Delt betaling – del kontant, del kort</td></tr>
            <tr><td><strong>Betal min del</strong></td><td>Hver gjest betaler for sine varer</td></tr>
        </tbody>
    </table>
    <p>I tillegg registreres korttyper: Debetkort, Kredittkort, Apple Pay og Google Pay.</p>

    <figure class="screenshot">
        <img :src="img06" alt="Betalingsskjerm">
        <figcaption>Figur 7: Betalingsskjermen viser totalbeløpet «Å betale» og tre betalingsmetoder: KORT, KONTANT og KONTANT OG KORT.</figcaption>
    </figure>

    <h3>7.2 Kontantbetaling</h3>
    <ol>
        <li>Velg «Kontant» som betalingsmetode</li>
        <li>Tast inn mottatt beløp på det numeriske tastaturet</li>
        <li>Systemet beregner og viser vekselbeløp automatisk</li>
        <li>Bekreft betaling</li>
        <li>Kassaskuffen åpnes automatisk</li>
        <li>Kvittering skrives ut</li>
    </ol>

    <figure class="screenshot">
        <img :src="img07" alt="Kontantbetaling">
        <figcaption>Figur 8: Kontantbetalingsdialogen. Totalbeløpet vises øverst (Kr 280). Operatøren taster inn mottatt kontantbeløp. «AVBRYT»-knappen avbryter betalingen.</figcaption>
    </figure>

    <h3>7.3 Kortbetaling</h3>
    <ol>
        <li>Velg «Kort» som betalingsmetode</li>
        <li>Betalingsterminalen (SoftPay/Viva) aktiveres</li>
        <li>Kunden holder/setter inn kort på terminalen</li>
        <li>Transaksjonsresultat vises (godkjent/avvist)</li>
        <li>Ved godkjent betaling skrives kvittering ut</li>
        <li>Ved avvist betaling vises feilmelding</li>
    </ol>

    <h3>7.4 Delt betaling (Kontant + Kort)</h3>
    <ol>
        <li>Velg «Kontant + Kort» som betalingsmetode</li>
        <li>Angi beløpet som skal betales kontant</li>
        <li>Restbeløpet behandles som kortbetaling</li>
        <li>Begge transaksjoner registreres</li>
    </ol>

    <h3>7.5 Betal min del (Delt regning)</h3>
    <ol>
        <li>Velg «Betal min del» som betalingsmetode</li>
        <li>Velg hvilke varer den aktuelle gjesten skal betale for</li>
        <li>Systemet beregner totalbeløpet for valgte varer</li>
        <li>Behandle betaling (kontant eller kort)</li>
        <li>Gjenta for neste gjest inntil hele ordren er betalt</li>
    </ol>

    <h3>7.6 Tips (Tips)</h3>
    <p>Dersom drikkepenger er aktivert for lokasjonen:</p>
    <ol>
        <li>Etter valg av betalingsmetode kan operatøren legge inn tips</li>
        <li>Tips legges til totalbeløpet</li>
        <li>Tips spores separat i rapporter</li>
        <li>Det er mulig å velge «Ingen tips»</li>
    </ol>

    <h3>7.7 Betalingsstatus</h3>
    <ul>
        <li><strong>Ikke betalt</strong> – Ingen betaling registrert</li>
        <li><strong>Betalt med kort</strong> – Fullstendig kortbetaling</li>
        <li><strong>Betalt kontant</strong> – Fullstendig kontantbetaling</li>
        <li><strong>Delt betaling</strong> – Kombinasjon av betalingsmetoder</li>
    </ul>
</div>

<!-- 8. Bordadministrasjon -->
<div class="section" id="kap8">
    <h2><span class="chapter-num">8</span> Bordadministrasjon</h2>

    <h3>8.1 Plantegninger</h3>
    <p>Systemet støtter flere plantegninger for restauranten, for eksempel: Første etasje, Andre etasje, Terrasse/uteplass. Hver plantegning administreres via et visuelt rutenett der bord kan plasseres.</p>

    <h3>8.2 Bordhåndtering</h3>
    <p>Hvert bord har følgende egenskaper:</p>
    <ul>
        <li><strong>Bordnummer/-navn</strong> – Unikt identifikator</li>
        <li><strong>Kapasitet</strong> – Antall gjester bordet har plass til</li>
    </ul>

    <figure class="screenshot">
        <img :src="img04" alt="Bordvalg">
        <figcaption>Figur 9: Bordvalgdialogen viser alle tilgjengelige bord (Bord 1–12) som oransje knapper. Operatøren trykker på ønsket bord for å tilordne ordren.</figcaption>
    </figure>

</div>

<!-- 9. Kvitteringer -->
<div class="section" id="kap9">
    <h2><span class="chapter-num">9</span> Kvitteringer og utskrift</h2>

    <h3>9.1 Kvitteringstyper</h3>
    <table>
        <thead><tr><th>Type</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Salgskvittering (SALE)</strong></td><td>Standard kvittering ved salg</td></tr>
            <tr><td><strong>Kopikvittering (COPY)</strong></td><td>Gjentrykk av tidligere kvittering</td></tr>
            <tr><td><strong>Returkvittering (REFUND)</strong></td><td>Kvittering ved retur/refundering</td></tr>
            <tr><td><strong>Ordrebeskrivelse (ORDER_DESCRIPTION)</strong></td><td>Kjøkkenbestilling</td></tr>
        </tbody>
    </table>

    <h3>9.2 Kvitteringsinnhold</h3>
    <h4>Topptekst:</h4>
    <ul>
        <li>Firmanavn og organisasjonsnummer</li>
        <li>Lokasjonsadresse og telefonnummer</li>
        <li>Dato, klokkeslett, operatør-ID og kvitteringsnummer</li>
    </ul>
    <h4>Ordrelinjer:</h4>
    <ul>
        <li>Varenavn, antall × enhetspris</li>
        <li>Tillegg/modifikatorer (egne linjer)</li>
        <li>Linjerabatter og kommentarer</li>
    </ul>
    <h4>Betalingsinformasjon:</h4>
    <ul>
        <li>Totalbeløp, betalingsmetode, MVA-spesifikasjon</li>
        <li>Vekselbeløp (kontant), korttransaksjons-ID (kort), tips</li>
    </ul>
    <h4>Bunntekst:</h4>
    <ul>
        <li>Takkmelding («Takk for besøket»)</li>
        <li>Systeminformasjon</li>
    </ul>

    <h3>9.3 Kjøkkenutskrift</h3>
    <p>Kjøkkenbestillinger skrives ut på dedikert kjøkkenskriver og inneholder: ordrenummer, ordretype, bordnummer (Spise Her), varenavn med tillegg, kommentarer og tidspunkt.</p>

</div>

<!-- 10. Kassaskuff -->
<div class="section" id="kap10">
    <h2><span class="chapter-num">10</span> Kassaskuff</h2>

    <h3>10.1 Automatisk åpning</h3>
    <p>Kassaskuffen åpnes automatisk ved kontantbetaling (for veksel).</p>

    <h3>10.2 Manuell åpning</h3>
    <p>Kassaskuffen kan åpnes manuelt via «ÅPNE KASSESKUFF» i toppmenyen. Alle åpninger logges i den elektroniske journalen.</p>

    <h3>10.3 Kassaskuffteller</h3>
    <p>Antall kassaskuffåpninger telles og rapporteres i Z-rapporten for sporingsformål.</p>

    <h3>10.4 Støttede kassaskuffer</h3>
    <ul>
        <li><strong>Sunmi innebygd</strong> kassaskuff</li>
        <li><strong>APG</strong> kassaskuff (ekstern)</li>
    </ul>
</div>

<!-- 11. Rapporter -->
<div class="section" id="kap11">
    <h2><span class="chapter-num">11</span> Rapporter</h2>

    <h3>11.1 X-rapport (Mellomrapport)</h3>
    <p>X-rapporten er en foreløpig rapport som kan kjøres når som helst i løpet av dagen uten å nullstille tellere.</p>
    <div class="info-box">
        <strong>X-rapporten inneholder:</strong> Salgsoversikt for inneværende skift, fordeling per betalingsmetode (kontant, kort), fordeling per ordretype (Spise Her, henting, levering), vare-/kategorisalgsanalyse, rabattoversikt og MVA-spesifikasjon.
    </div>

    <h3>11.2 Z-rapport (Dagsoppgjør)</h3>
    <p>Z-rapporten er den offisielle dagsavslutningsrapporten som genereres ved dagslutt.</p>

    <h4>Kontantoppgjør:</h4>
    <ul>
        <li>Åpningsbeløp (kontanter ved dagstart)</li>
        <li>Sluttsaldo (talt beløp ved dagslutt)</li>
        <li>Kontantuttak i løpet av dagen</li>
        <li>Differanse/avvik med kommentarfelt</li>
    </ul>

    <h4>Salg per betalingsmetode og ordretype:</h4>
    <table>
        <thead><tr><th>Kombinasjon</th><th>Detaljer</th></tr></thead>
        <tbody>
            <tr><td>Spise Her (Kontant / Kort)</td><td>Antall, MVA, bruttobeløp</td></tr>
            <tr><td>Henting (Kontant / Kort)</td><td>Antall, MVA, bruttobeløp</td></tr>
            <tr><td>Levering (Kontant / Kort)</td><td>Antall, MVA, bruttobeløp</td></tr>
            <tr><td>Leveringstjeneste (gebyr)</td><td>Antall, MVA, bruttobeløp</td></tr>
        </tbody>
    </table>

    <h4>Tellere:</h4>
    <ul>
        <li>Antall salgskvitteringer, kopikvitteringer, returkvitteringer, annullerte kvitteringer</li>
        <li>Antall kassaskuffåpninger</li>
    </ul>

    <h4>Øvrig:</h4>
    <ul>
        <li>Totalrefusjoner, rabattotalbeløp og antall, ufullstendige salg</li>
        <li>Varegruppefordeling</li>
        <li>Grand totals (akkumulerte totaler som aldri nullstilles)</li>
        <li>Unikt Z-rapportnummer per terminal per dag</li>
        <li>Dagstart-/dagsluttoperatør</li>
    </ul>
</div>

<!-- 12. Dagstart og dagslutt -->
<div class="section" id="kap12">
    <h2><span class="chapter-num">12</span> Dagstart og dagslutt</h2>

    <h3>12.1 Dagstart (Beginning of Day)</h3>
    <p>Ved dagstart gjennomføres følgende:</p>
    <ol>
        <li>Operatøren logger inn med PIN-kode</li>
        <li><strong>Registrering av åpningsbeløp</strong> – Operatøren teller og taster inn kontantbeløpet i kassaskuffen</li>
        <li>Beløpet registreres som åpningsbalanse for dagen</li>
        <li>Kassen er klar for salg</li>
    </ol>

    <h3>12.2 Dagslutt (End of Day)</h3>
    <p>Dagsluttrutinen gjennomføres ved slutten av arbeidsdagen:</p>
    <ol>
        <li><strong>Fullfør alle åpne ordrer</strong> – Sørg for at alle ordrer er betalt eller kansellert</li>
        <li><strong>Registrer eventuelle kontantuttak</strong> (dersom aktuelt)</li>
        <li><strong>Start dagslutt</strong> fra toppmenyen («TA DAGSOPPGJØR»)</li>
        <li><strong>Tell kontanter</strong> – Tast inn det talte kontantbeløpet i kassaskuffen</li>
        <li><strong>Gjennomgå avvik</strong> – Systemet beregner eventuell differanse</li>
        <li><strong>Bekreft dagslutt</strong> – Operatøren bekrefter med navn/PIN</li>
        <li><strong>Z-rapport genereres</strong> – Rapporten lagres lokalt og lastes opp til skytjenesten</li>
        <li><strong>Dagen er avsluttet</strong> – Ingen flere transaksjoner kan registreres</li>
    </ol>

    <figure class="screenshot">
        <img :src="img11" alt="Dagsoppgjør – Kontantuttak">
        <figcaption>Figur 10: Første steg i dagsoppgjøret. Systemet spør om det er gjort kontantuttak fra kassen. Velg «NEI, GÅ VIDERE» eller «JA» for å registrere uttak.</figcaption>
    </figure>

    <figure class="screenshot">
        <img :src="img12" alt="Dagsoppgjør – Opptelling">
        <figcaption>Figur 11: Opptellingsskjermen i dagsoppgjøret. Operatøren taster inn talt kontantbeløp. Høyre side viser startbeholdning, korttransaksjoner og kontanter i kassen. Differansen beregnes automatisk.</figcaption>
    </figure>
</div>

<!-- 13. Kontantuttak -->
<div class="section" id="kap13">
    <h2><span class="chapter-num">13</span> Kontantuttak</h2>
    <p>Kontantuttak brukes for å registrere uttak av kontanter fra kassaskuffen i løpet av arbeidsdagen.</p>
    <h3>Slik gjøres kontantuttak:</h3>
    <ol>
        <li>Naviger til <strong>TA DAGSOPPGJØR</strong> fra toppmenyen</li>
        <li>Velg <strong>JA</strong> på spørsmålet om kontantuttak</li>
        <li>Tast inn beløpet som tas ut på det numeriske tastaturet</li>
        <li>Bekreft uttaket</li>
        <li>Uttaket registreres og medregnes i Z-rapporten ved dagslutt</li>
    </ol>
</div>

<!-- 14. Kansellering og retur -->
<div class="section" id="kap14">
    <h2><span class="chapter-num">14</span> Kansellering og retur</h2>

    <h3>14.1 Kansellering av ubetalte ordrer</h3>
    <ol>
        <li>Trykk <strong>KANSELLER ORDRE</strong> i toppmenyen</li>
        <li>Systemet viser en liste over ubetalte ordrer</li>
        <li>Velg ordren(e) som skal kanselleres</li>
        <li>Bekreft kansellering</li>
        <li>Ordren markeres som <strong>KANSELLERT</strong></li>
        <li>Kanselleringen logges i den elektroniske journalen</li>
    </ol>

    <figure class="screenshot">
        <img :src="img09" alt="Kanseller ordre">
        <figcaption>Figur 12: Kanselleringsskjermen med forklaringstekst. Viser kun ubetalte ordrer som ikke allerede er kansellert. For betalte ordrer henvises det til returfunksjonen.</figcaption>
    </figure>

    <h3>14.2 Retur/refundering av betalte ordrer</h3>
    <ol>
        <li>Trykk <strong>REFUNDER ORDRE</strong> i toppmenyen</li>
        <li>Systemet viser en liste over betalte ordrer som ikke er avregnet</li>
        <li>Velg ordren(e) som skal refunderes</li>
        <li>Gå videre til refunderingsskjermen</li>
        <li>For kortbetaling: Refunderingen behandles via betalingsterminalen</li>
        <li>For kontantbetaling: Kontantbeløpet returneres</li>
        <li>Ordren markeres som <strong>REFUNDERT</strong> eller <strong>DELVIS REFUNDERT</strong></li>
    </ol>

    <figure class="screenshot">
        <img :src="img10" alt="Refunder ordre">
        <figcaption>Figur 13: Refunderingsskjermen med forklaringstekst. Viser kun betalte ordrer som ikke er tatt med i dagsoppgjør og som ikke allerede er refundert.</figcaption>
    </figure>
</div>

<!-- 15. Kundedisplay -->
<div class="section" id="kap15">
    <h2><span class="chapter-num">15</span> Kundedisplay</h2>

    <h3>15.1 Funksjonalitet</h3>
    <p>TastyPOS støtter dobbeltskjerm på Sunmi-enheter med dedikert kundedisplay:</p>
    <ul>
        <li><strong>Annonserotasjon</strong> – Vis reklamemateriell/bilder som roterer på kundeskjermen</li>
        <li><strong>Ordrebekreftelse</strong> – Vis ordredetaljer for kunden under bestilling</li>
        <li><strong>Takkmelding</strong> – Vis takkemelding etter fullført betaling</li>
    </ul>

    <h3>15.2 Administrasjon</h3>
    <p>Kundedisplayet administreres automatisk av systemet via Presentation API og aktiveres når en kompatibel Sunmi-enhet med dobbeltskjerm brukes.</p>
</div>

<!-- 16. Elektronisk journal -->
<div class="section" id="kap16">
    <h2><span class="chapter-num">16</span> Elektronisk journal</h2>

    <h3>16.1 Hendelseslogging</h3>
    <p>Systemet logger følgende hendelsestyper automatisk:</p>
    <table>
        <thead><tr><th>Hendelse</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>POS_START</strong></td><td>Systemet startes opp</td></tr>
            <tr><td><strong>POS_END</strong></td><td>Systemet avsluttes</td></tr>
            <tr><td><strong>EMPLOYEE_LOGIN</strong></td><td>Operatør logger inn</td></tr>
            <tr><td><strong>EMPLOYEE_LOGOUT</strong></td><td>Operatør logger ut</td></tr>
            <tr><td><strong>CASH_DRAWER_OPEN</strong></td><td>Kassaskuff åpnes</td></tr>
            <tr><td><strong>POS_UPDATE</strong></td><td>Programvareoppdatering</td></tr>
            <tr><td><strong>SALE_RECEIPT</strong></td><td>Salgskvittering skrevet ut</td></tr>
            <tr><td><strong>RETURN_RECEIPT</strong></td><td>Returkvittering skrevet ut</td></tr>
            <tr><td><strong>Z_REPORT</strong></td><td>Z-rapport generert</td></tr>
        </tbody>
    </table>
    <p>Hver hendelse lagres med: tidsstempel, hendelsestype, operatør-ID, terminal-ID, forhandler-/lokasjonsinformasjon og transaksjons-ID (for kvitteringer).</p>

    <h3>16.2 Kontanttransaksjonslogging</h3>
    <p>For hvert salg registreres:</p>
    <ul>
        <li><strong>CashTransaction</strong> – Hoveddokument per salg</li>
        <li><strong>CashTransactionLine</strong> – En linje per vare i salget</li>
        <li><strong>CashTransactionPayment</strong> – En linje per betalingsmetode</li>
    </ul>

    <h3>16.3 MVA-sporbarhet</h3>
    <p>Systemet sporer MVA gjennom hele transaksjonskjeden:</p>
    <ul>
        <li>MVA-sats per vare (15 %, 25 %)</li>
        <li>MVA-beregning per handlekurvlinje og ordre-aggregert MVA</li>
        <li>MVA per betalingsmetode</li>
        <li>Rabattens innvirkning på MVA</li>
        <li>MVA-reversering ved retur</li>
    </ul>
</div>

<!-- 17. SAF-T -->
<div class="section" id="kap17">
    <h2><span class="chapter-num">17</span> SAF-T kassasystem</h2>

    <h3>17.1 Oversikt</h3>
    <p>TastyPOS er i samsvar med norske krav til SAF-T Kassasystem (Standard Audit File – Tax) som spesifisert av Skatteetaten.</p>

    <h3>17.2 Funksjonalitet</h3>
    <ul>
        <li><strong>XML-eksport</strong> – Genererer SAF-T-kompatible XML-filer med transaksjonsdata</li>
        <li><strong>Datoperiodevalg</strong> – Velg tidsperiode for eksporten</li>
        <li><strong>Integrasjon med elektronisk journal</strong> – Data hentes fra den elektroniske journalen</li>
        <li><strong>Tilkobling kreves</strong> – Internettilkobling er nødvendig for eksport</li>
    </ul>

    <h3>17.3 Dataelementer som eksporteres</h3>
    <p>I henhold til SAF-T Kassasystem v1.2:</p>
    <ul>
        <li>Organisasjonsnummer (MVA-nummer) og terminal-/kasse-ID</li>
        <li>Operatøridentifikasjon og kvitteringsnumre</li>
        <li>Transaksjonsbeløp (brutto, MVA, netto)</li>
        <li>Betalingsmetodeklassifisering og dato-/klokkeslettstempel</li>
        <li>Korreksjoner, annulleringer, åpningsbalanse</li>
        <li>Kassaskuffåpninger, prisforespørsler, returhåndtering</li>
    </ul>

    <h3>17.4 Eksport</h3>
    <p>Eksport av SAF-T gjøres i Backoffice under SAF-T Rapport:</p>
    <ol>
        <li>Logg inn i Backoffice</li>
        <li>Naviger til <strong>SAF-T Rapport</strong></li>
        <li>Velg ønsket datoperiode</li>
        <li>Systemet genererer SAF-T-filen</li>
        <li>Filen er klar for innsending til skattemyndighetene</li>
    </ol>
</div>

<!-- 18. Maskinvare -->
<div class="section" id="kap18">
    <h2><span class="chapter-num">18</span> Maskinvareintegrasjon</h2>

    <h3>18.1 Sunmi-enheter</h3>
    <table>
        <thead><tr><th>Modell</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Sunmi V2s</strong></td><td>Håndholdt terminal med innebygd skriver</td></tr>
            <tr><td><strong>Sunmi D3 Mini</strong></td><td>Kompakt skranketilpasset terminal</td></tr>
            <tr><td><strong>Sunmi V3 Mix</strong></td><td>Avansert terminal med dobbeltskjerm</td></tr>
        </tbody>
    </table>
    <p><strong>Integrerte funksjoner:</strong> Termisk kvitteringsskriver, kassaskuff via skrivergrensesnitt, dobbeltskjerm/kundedisplay, nettverkstilkobling (Wi-Fi/mobil).</p>

    <h3>18.2 Betalingsterminaler</h3>
    <h4>SoftPay:</h4>
    <ul>
        <li>Kontaktløs kortbetaling via NFC</li>
        <li>Apple Pay og Google Pay-støtte</li>
        <li>Tips via terminal</li>
        <li>Feilhåndtering (tidsavbrudd, avbrutt tilkobling, avvist kort)</li>
    </ul>
    <h4>Viva:</h4>
    <ul>
        <li>Alternativ kortbetalingsløsning</li>
        <li>Integrasjon via betalingsapp</li>
        <li>Refunderingshåndtering</li>
    </ul>

    <h3>18.3 Kassaskuffer</h3>
    <ul>
        <li><strong>Sunmi innebygd</strong> – Via skrivergrensesnittet</li>
        <li><strong>APG</strong> – Ekstern kassaskuff</li>
    </ul>
</div>

<!-- 19. Innstillinger -->
<div class="section" id="kap19">
    <h2><span class="chapter-num">19</span> Innstillinger og konfigurasjon</h2>

    <h3>19.1 Terminalinnstillinger</h3>
    <table>
        <thead><tr><th>Innstilling</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td>System-ID-nummer</td><td>Organisasjonsnummer</td></tr>
            <tr><td>Terminal-ID</td><td>Unikt kassaregister-ID</td></tr>
            <tr><td>Terminaltype</td><td>Spesifikasjon av terminaltype</td></tr>
            <tr><td>Lokasjon</td><td>Tilordnet filial/lokasjon</td></tr>
            <tr><td>Skriver</td><td>Skrivervalg og testutskrift</td></tr>
        </tbody>
    </table>

    <h3>19.2 Lokasjonsinnstillinger</h3>
    <table>
        <thead><tr><th>Innstilling</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td>Lokasjonsnavn</td><td>Navn på filialen</td></tr>
            <tr><td>MVA-nummer</td><td>Organisasjonsnummer</td></tr>
            <tr><td>E-postadresse</td><td>Kontakt-e-post</td></tr>
            <tr><td>Adresse</td><td>Gateadresse, postnummer, by</td></tr>
            <tr><td>Telefonnummer</td><td>Kontakttelefon</td></tr>
            <tr><td>Betalingsleverandør</td><td>Valg av nettbetalingsleverandør</td></tr>
        </tbody>
    </table>

    <h3>19.3 Betalingsinnstillinger</h3>
    <table>
        <thead><tr><th>Innstilling</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td>Godta kontantbetaling</td><td>Aktiver/deaktiver kontantbetaling</td></tr>
            <tr><td>Betalingsterminal</td><td>Velg SoftPay eller Viva</td></tr>
            <tr><td>Tips</td><td>Aktiver/deaktiver tips</td></tr>
            <tr><td>Delt betaling</td><td>Aktiver/deaktiver delt betaling</td></tr>
            <tr><td>Betal min del</td><td>Aktiver/deaktiver «betal min del»</td></tr>
        </tbody>
    </table>

    <h3>19.4 Ordretypeinnstillinger</h3>
    <table>
        <thead><tr><th>Innstilling</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td>Spise Her</td><td>Aktiver/deaktiver</td></tr>
            <tr><td>Henting</td><td>Aktiver/deaktiver</td></tr>
            <tr><td>Levering</td><td>Aktiver/deaktiver</td></tr>
            <tr><td>Minstbeløp levering</td><td>Minimumsbeløp for leveringsordre</td></tr>
            <tr><td>Leveringsdager</td><td>Dager levering er tilgjengelig</td></tr>
        </tbody>
    </table>

    <h3>19.5 Funksjonsbrytere</h3>
    <table>
        <thead><tr><th>Innstilling</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td>Rabatt</td><td>Aktiver/deaktiver rabattfunksjon</td></tr>
            <tr><td>Lojalitetsprogram</td><td>Aktiver/deaktiver lojalitetspoeng</td></tr>
            <tr><td>Tillegg</td><td>Aktiver/deaktiver tilleggsvalg</td></tr>
            <tr><td>Menykategorier</td><td>Aktiver/deaktiver kategorivisning</td></tr>
            <tr><td>Kundedisplay</td><td>Aktiver/deaktiver dobbeltskjerm</td></tr>
            <tr><td>SAF-T-eksport</td><td>Aktiver/deaktiver SAF-T-funksjon</td></tr>
        </tbody>
    </table>
</div>

<!-- 20. Synkronisering -->
<div class="section" id="kap20">
    <h2><span class="chapter-num">20</span> Synkronisering og skytjeneste</h2>

    <h3>20.1 CAGISTA Cloud</h3>
    <p>TastyPOS synkroniserer data med CAGISTA Cloud via Couchbase Sync Gateway:</p>
    <ul>
        <li><strong>Automatisk synkronisering</strong> – Data synkroniseres automatisk i bakgrunnen</li>
        <li><strong>Synkroniseringsintervall</strong> – Hvert 30. sekund</li>
        <li><strong>Første synkronisering</strong> – 3 minutters forsinkelse ved oppstart</li>
        <li><strong>Offline-modus</strong> – Systemet fungerer fullt ut uten nettverkstilkobling; data synkroniseres når tilkoblingen gjenopprettes</li>
        <li><strong>Online-indikator</strong> – Visuell indikasjon på tilkoblingsstatus (grønt ikon)</li>
    </ul>

    <h3>20.2 Synkroniserte data</h3>
    <p>All data synkroniseres mellom terminal og sky:</p>
    <ul>
        <li>Meny og produkter</li>
        <li>Ordrer og betalinger</li>
        <li>Z-rapporter</li>
        <li>Elektronisk journal</li>
        <li>Brukerinformasjon</li>
        <li>Konfigurasjon og innstillinger</li>
    </ul>
</div>


<!-- 22. Arbeidsflyter -->
<div class="section" id="kap21">
    <h2><span class="chapter-num">21</span> Arbeidsflyter</h2>

    <h3>22.1 Komplett salgsflyt</h3>
    <div class="workflow">
        <ol>
            <li>Logg inn (PIN-kode)</li>
            <li>Velg ordretype (Spise Her / Henting / Levering)</li>
            <li>Velg bord (kun Spise Her)</li>
            <li>Bla gjennom menyen etter kategori</li>
            <li>Velg varer og eventuelle tillegg</li>
            <li>Legg til i handlekurven</li>
            <li>Påfør rabatt (valgfritt)</li>
            <li>Gå til betaling</li>
            <li>Velg betalingsmetode</li>
            <li>Behandle betaling</li>
            <li>Kvittering skrives ut</li>
            <li>Kassaskuff åpnes (ved kontant)</li>
            <li>Transaksjonen logges i elektronisk journal</li>
        </ol>
    </div>

    <h3>22.2 Returflyt</h3>
    <div class="workflow">
        <ol>
            <li>Trykk REFUNDER ORDRE i toppmenyen</li>
            <li>Vis liste over betalte, ikke-avregnede ordrer</li>
            <li>Velg ordre(r) for retur</li>
            <li>Gå til refunderingsskjerm</li>
            <li>Behandle refundering via terminal (kort) eller kontant</li>
            <li>Ordren markeres som REFUNDERT</li>
        </ol>
    </div>

    <h3>22.3 Dagsluttflyt</h3>
    <div class="workflow">
        <ol>
            <li>Fullfør alle ordrer og betalinger</li>
            <li>Registrer eventuelle kontantuttak</li>
            <li>Trykk TA DAGSOPPGJØR i toppmenyen</li>
            <li>Tast inn talt kontantbeløp</li>
            <li>Gjennomgå eventuelle avvik</li>
            <li>Bekreft med operatørnavn/PIN</li>
            <li>Z-rapport genereres og lastes opp</li>
            <li>Dagen er avsluttet</li>
        </ol>
    </div>

    <h3>22.4 Kanselleringsflyt</h3>
    <div class="workflow">
        <ol>
            <li>Trykk KANSELLER ORDRE i toppmenyen</li>
            <li>Vis liste over ubetalte ordrer</li>
            <li>Velg ordre(r) for kansellering</li>
            <li>Bekreft kansellering</li>
            <li>Ordren markeres som KANSELLERT</li>
            <li>Hendelsen logges</li>
        </ol>
    </div>
</div>

<!-- Tillegg: Lojalitetsprogram -->
<div class="section">
    <h2><span class="chapter-num">+</span> Tillegg: Lojalitetsprogram</h2>
    <p>Dersom lojalitetsprogrammet er aktivert:</p>
    <ul>
        <li>Kunder kan samle lojalitetspoeng</li>
        <li>Poeng kan innløses som rabatt ved betaling</li>
        <li>Poengfradrag beregnes med korrekt MVA-innvirkning</li>
        <li>Lojalitetsrabatter spores separat i rapporter</li>
    </ul>
</div>

</main>
</div>

<!-- Footer -->
<div class="footer">
    <p>Denne brukerveiledningen beskriver systemets oppbygging og funksjonalitet per januar 2026.</p>
    <p>For oppdateringer og endringer, kontakt <strong>CAGISTA AS</strong>.</p>
</div>
</div>
</template>

<style>
.bv-wrapper {
    /*
     * Brand accents. A FILL is identity and never moves — an orange button is orange in both themes —
     * but the same hue AS TYPE has to, because #E65100 on a near-black page is not legible. Same split
     * the --bo-*-ink tokens make, and the reason this palette is not simply four colours.
     */
    --orange: #F57C00;
    --orange-press: #E65100;    /* the accent as a pressed fill */
    --accent-ink: #E65100;      /* the accent as TYPE */
    --tint: #FFF3E0;            /* the pale accent panel behind a callout */
    --green: #4CAF50;
    --refund-ink: #6A1B9A;

    /*
     * The two dark bands — the page header and the table head — are dark in BOTH themes, so they hold
     * literal values and the text on them is literally white. Reading either off a surface token is
     * what broke this page: --white resolves to #1c1f24 in dark, so the table head was near-black text
     * on near-black, and the header gradient ran from dark to the near-WHITE ink colour.
     */
    --band: #2C2C2C;
    --band-2: #1a1a2e;
    --on-band: #ffffff;

    --ink: var(--bo-ink);       /* headings and emphasis */
    --gray-100: var(--bo-surface-2);
    --gray-200: var(--bo-line);
    --gray-300: var(--bo-line);
    --gray-600: var(--bo-muted);
    --gray-800: var(--bo-ink-2);   /* body copy */
    --card: var(--bo-surface);     /* CARD SURFACES ONLY — type on a band uses --on-band */
    --shadow: 0 2px 8px rgba(0,0,0,0.08);
    --shadow-lg: 0 4px 16px rgba(0,0,0,0.12);
    --radius: 8px;

    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.7;
    color: var(--gray-800);
    background: var(--gray-100);
    scroll-behavior: smooth;
}

/* Only the roles that cannot keep their light value: an accent used as type, and the pale panel it
   sits on. Everything else already comes from the --bo-* tokens, which move on their own. */
[data-bs-theme="dark"] .bv-wrapper {
    --accent-ink: #FFA552;
    --tint: #3A2412;
    --refund-ink: #D6A8EE;
}

.bv-wrapper *, .bv-wrapper *::before, .bv-wrapper *::after {
    box-sizing: border-box;
}

/* Header */
.bv-wrapper .header {
    background: linear-gradient(135deg, var(--band) 0%, var(--band-2) 100%);
    color: var(--on-band);
    padding: 48px 0 40px;
    text-align: center;
    position: relative;
}
.bv-wrapper .header::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 0; right: 0;
    height: 40px;
    background: var(--gray-100);
    border-radius: 50% 50% 0 0 / 100% 100% 0 0;
}
.bv-wrapper .header h1 {
    font-size: 2.4em;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
}
.bv-wrapper .header .subtitle {
    font-size: 1.15em;
    opacity: 0.85;
    font-weight: 300;
}
.bv-wrapper .header-meta {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 32px;
    font-size: 0.9em;
    opacity: 0.7;
}
.bv-wrapper .pdf-download {
    color: var(--on-band);
    background: var(--orange);
    padding: 6px 16px;
    border-radius: 20px;
    text-decoration: none;
    font-weight: 600;
    opacity: 1;
    transition: background 0.2s;
}
.bv-wrapper .pdf-download:hover {
    background: var(--orange-press);
}

/* Layout */
.bv-wrapper .container {
    display: flex;
    max-width: 1320px;
    margin: 0 auto;
    padding: 0 24px;
    gap: 40px;
}

/* Sidebar TOC */
.bv-wrapper .sidebar {
    width: 280px;
    flex-shrink: 0;
    position: sticky;
    top: calc(var(--bo-nav-offset, 0px) + 24px);   /* clears the pinned navbar */
    align-self: flex-start;
    max-height: calc(100vh - var(--bo-nav-offset, 0px) - 48px);
    overflow-y: auto;
    padding: 24px 0;
}
.bv-wrapper .sidebar::-webkit-scrollbar { width: 4px; }
.bv-wrapper .sidebar::-webkit-scrollbar-thumb { background: var(--gray-300); border-radius: 4px; }

.bv-wrapper .toc {
    background: var(--card);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    padding: 20px;
}
.bv-wrapper .toc h3 {
    font-size: 0.85em;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--gray-600);
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid var(--orange);
}
.bv-wrapper .toc ol {
    list-style: none;
    counter-reset: toc-counter;
    padding-left: 0;
}
.bv-wrapper .toc ol li {
    counter-increment: toc-counter;
    list-style: none;
    display: block !important;
    width: 100% !important;
    float: none !important;
}
.bv-wrapper .toc ol li a {
    display: block;
    padding: 6px 8px;
    color: var(--gray-800);
    text-decoration: none;
    font-size: 0.88em;
    border-radius: 4px;
    transition: all 0.2s;
    border-left: 3px solid transparent;
}
.bv-wrapper .toc ol li a:hover {
    background: var(--tint);
    color: var(--accent-ink);
    border-left-color: var(--orange);
}
.bv-wrapper .toc ol li a::before {
    content: counter(toc-counter) ". ";
    font-weight: 600;
    color: var(--orange);
}

/* Main content */
.bv-wrapper .main {
    flex: 1;
    min-width: 0;
    padding: 24px 0 80px;
}

/* Sections */
.bv-wrapper .section {
    background: var(--card);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    padding: 40px 48px;
    margin-bottom: 24px;
}

.bv-wrapper h2 {
    font-size: 1.6em;
    color: var(--ink);
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 3px solid var(--orange);
    display: flex;
    align-items: center;
    gap: 12px;
}
.bv-wrapper h2 .chapter-num {
    background: var(--orange);
    color: var(--on-band);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6em;
    font-weight: 700;
    flex-shrink: 0;
}

.bv-wrapper h3 {
    display: block;
    width: 100%;
    font-size: 1.15em;
    color: var(--accent-ink);
    margin: 28px 0 12px;
}
.bv-wrapper h3:first-of-type { margin-top: 0; }

.bv-wrapper h4 {
    font-size: 1em;
    color: var(--gray-800);
    margin: 20px 0 8px;
    font-weight: 600;
}

.bv-wrapper p { margin-bottom: 12px; }

/* Lists */
.bv-wrapper .section ul,
.bv-wrapper .section ol {
    display: block !important;
    margin: 8px 0 16px 24px !important;
    padding-left: 20px !important;
    float: none !important;
    flex-direction: column !important;
}
.bv-wrapper .section ul { list-style-type: disc !important; }
.bv-wrapper .section ol { list-style-type: decimal !important; }
.bv-wrapper .section li {
    display: list-item !important;
    float: none !important;
    width: 100% !important;
    margin-bottom: 4px !important;
}
.bv-wrapper .section > ul > li,
.bv-wrapper .section > ol > li {
    padding: 0 !important;
}
.bv-wrapper .section ul > li { list-style-type: disc !important; }
.bv-wrapper .section ol > li { list-style-type: decimal !important; }
.bv-wrapper .workflow ol { list-style: none !important; padding-left: 0 !important; }
.bv-wrapper .workflow ol > li { list-style: none !important; list-style-type: none !important; }
.bv-wrapper .workflow ol > li::marker { content: none !important; font-size: 0 !important; }
.bv-wrapper li strong { color: var(--ink); }

/* Tables */
.bv-wrapper table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 0.95em;
}
.bv-wrapper thead th {
    background: var(--band);
    color: var(--on-band);
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 0.9em;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.bv-wrapper thead th:first-child { border-radius: var(--radius) 0 0 0; }
.bv-wrapper thead th:last-child { border-radius: 0 var(--radius) 0 0; }
.bv-wrapper tbody td {
    padding: 10px 16px;
    border-bottom: 1px solid var(--gray-200);
}
.bv-wrapper tbody tr:hover { background: var(--tint); }
.bv-wrapper tbody tr:last-child td:first-child { border-radius: 0 0 0 var(--radius); }
.bv-wrapper tbody tr:last-child td:last-child { border-radius: 0 0 var(--radius) 0; }

/* Screenshots */
.bv-wrapper .screenshot {
    margin: 24px 0;
    text-align: center;
}
.bv-wrapper .screenshot img {
    max-width: 100%;
    border-radius: var(--radius);
    box-shadow: var(--shadow-lg);
    border: 1px solid var(--gray-200);
}
.bv-wrapper .screenshot figcaption {
    margin-top: 10px;
    font-size: 0.88em;
    color: var(--gray-600);
    font-style: italic;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
}

/* Info boxes */
.bv-wrapper .info-box {
    background: var(--tint);
    border-left: 4px solid var(--orange);
    padding: 16px 20px;
    border-radius: 0 var(--radius) var(--radius) 0;
    margin: 16px 0;
    font-size: 0.95em;
}

/* Workflow steps */
.bv-wrapper .workflow {
    background: var(--gray-100);
    border-radius: var(--radius);
    padding: 24px 28px;
    margin: 16px 0;
}
.bv-wrapper .workflow ol {
    margin-left: 0;
    list-style: none !important;
    list-style-type: none !important;
    counter-reset: step-counter;
}
.bv-wrapper .workflow ol li {
    counter-increment: step-counter;
    padding: 8px 0 8px 48px;
    position: relative;
    border-left: 2px solid var(--gray-300);
    margin-left: 16px;
    margin-bottom: 0;
    list-style: none !important;
    list-style-type: none !important;
}
.bv-wrapper .workflow ol li:last-child { border-left-color: transparent; }
.bv-wrapper .workflow ol li::before {
    content: counter(step-counter);
    position: absolute;
    left: -14px;
    top: 6px;
    background: var(--orange);
    color: var(--on-band);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8em;
    font-weight: 700;
}

/* Status flow */
.bv-wrapper .status-flow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin: 16px 0;
    padding: 16px;
    background: var(--gray-100);
    border-radius: var(--radius);
}
.bv-wrapper .status-badge {
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.85em;
    font-weight: 600;
    white-space: nowrap;
}
.bv-wrapper .status-draft { background: var(--bo-info-bg); color: var(--bo-info-fg); }
.bv-wrapper .status-ordered { background: var(--bo-brand-tint); color: var(--bo-brand-ink); }
.bv-wrapper .status-confirmed { background: var(--bo-ok-bg); color: var(--bo-success-ink); }
.bv-wrapper .status-completed { background: var(--bo-ok-bg); color: var(--bo-ok-fg); }
.bv-wrapper .status-cancelled { background: var(--bo-surface-2); color: var(--bo-alert-ink); }
.bv-wrapper .status-refunded { background: var(--bo-info-bg); color: var(--refund-ink); }
.bv-wrapper .status-arrow {
    font-size: 1.2em;
    color: var(--gray-600);
}

/* Footer */
.bv-wrapper .footer {
    text-align: center;
    padding: 32px 24px;
    color: var(--gray-600);
    font-size: 0.9em;
    border-top: 1px solid var(--gray-200);
    margin-top: 40px;
}

/* Responsive */
@media (max-width: 960px) {
    .bv-wrapper .container { flex-direction: column; }
    .bv-wrapper .sidebar {
        width: 100%;
        position: static;
        max-height: none;
        padding: 0;
    }
    .bv-wrapper .section { padding: 24px; }
    .bv-wrapper .header h1 { font-size: 1.8em; }
    .bv-wrapper .header-meta { flex-direction: column; gap: 4px; }
}

@media print {
    .bv-wrapper .sidebar { display: none; }
    .bv-wrapper .section { box-shadow: none; break-inside: avoid; }
    .bv-wrapper .header::after { display: none; }
}
</style>
